// Recovery for failed dynamic imports (JURYBOX-WEB-APP-16). A lazy chunk
// fetch can fail transiently — a flaky connection, a proxy hiccup — and
// without a retry the failure is terminal for whatever the chunk carried:
// the App bootstrap shows its couldn't-load screen, a React.lazy site
// throws to the nearest boundary. One paced retry absorbs the transient
// case; anything that fails twice is treated as real and rejects to the
// caller's existing handling.

const RETRY_DELAY_MS = 1000;

/**
 * Run a dynamic-import thunk, retrying once after a short pause when the
 * first attempt fails.
 *
 * @param {() => Promise<any>} importer - e.g. () => import('./components/App')
 * @param {number} [retries] - additional attempts after the first failure
 * @returns {Promise<any>} the module namespace, or the LAST failure
 */
export async function retryImport(importer, retries = 1) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY_MS),
      );
    }
    try {
      const result = await importer();
      if (result === undefined) {
        // Vite's preload helper RESOLVES (undefined) instead of rejecting
        // when a 'vite:preloadError' listener called preventDefault() —
        // i.e. installPreloadErrorReload below is mid-reload. A module
        // namespace is never undefined, so treat this as "reload in
        // flight" and never settle: settling would run the call site
        // against undefined while the page is being replaced (a flashed
        // terminal error screen and a spurious Sentry event).
        return new Promise(() => {});
      }
      return result;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

// One reload per session, so a persistent failure (a broken deploy, an
// offline client) cannot reload-loop; the second failure falls through to
// Vite's default behavior — the import rejects and the call site's own
// error handling reports it.
const PRELOAD_RELOAD_KEY = 'jurybox-preload-error-reloaded';

/**
 * Install the app-wide recovery for Vite's chunk/CSS preload failures.
 * Vite dispatches 'vite:preloadError' when a dynamically imported chunk
 * (or its CSS) fails to fetch; a reload re-resolves the entry point and
 * its import graph. This usually also heals the stale-tab case (a
 * pre-deploy index whose chunks left the CDN edge) — with one known
 * exception: a tab still controlled by an OLD service worker can be
 * served the same stale index again, in which case the second failure
 * finds the guard spent and falls through to normal rejection (the same
 * end state as before this handler existed).
 *
 * The reload is deliberate data-shedding: non-persisted state (undo
 * stacks, multi-select) is lost. Case data itself lives in Dexie, so
 * nothing durable is lost, and a broken lazy surface is strictly worse.
 *
 * The contract with retryImport above: preventDefault() makes Vite's
 * helper RESOLVE the failed import with undefined, so EVERY dynamic
 * import in the app must go through retryImport (which parks an
 * undefined resolution forever instead of running the call site against
 * it). A conformance test pins this.
 *
 * Storage access is guarded: sessionStorage can throw (blocked site data,
 * some private modes), and recovery code must never introduce its own
 * crash — with no usable storage we fail open to Vite's default (no
 * reload).
 */
export function installPreloadErrorReload({
  storage = () => window.sessionStorage,
  reload = () => window.location.reload(),
} = {}) {
  // Once a reload has been initiated, swallow every further preload
  // failure in this dying page: their imports resolve undefined and
  // retryImport parks them, instead of a second failure racing the
  // reload into a terminal error screen + spurious Sentry event.
  let reloadInFlight = false;

  // Note the layering: for a chunk failure that goes through Vite's
  // preload helper, THIS handler wins first — retryImport's retry only
  // runs for failures the helper doesn't mediate, or once the session
  // guard below is spent and the event falls through as a rejection.
  const handler = (event) => {
    if (reloadInFlight) {
      event.preventDefault();
      return;
    }
    let reloadArmed = false;
    try {
      const store = storage();
      if (store.getItem(PRELOAD_RELOAD_KEY) !== '1') {
        store.setItem(PRELOAD_RELOAD_KEY, '1');
        // Armed only AFTER the write landed: a storage that reads but
        // throws on write (full session store, some private modes) must
        // not reload, or a persistent failure loops forever with the
        // guard never persisted.
        reloadArmed = true;
      }
    } catch (e) {
      // Storage unreadable or unwritable — cannot bound a reload loop,
      // so fail open to Vite's default rejection.
    }
    if (reloadArmed) {
      // Reload FIRST, preventDefault second: if reload() itself throws
      // (an embedding that blocks it), swallowing the rejection would
      // leave a permanently blank page with no error signal anywhere —
      // fall through to the default rejection instead. The session
      // guard stays burned either way; a storage rollback here could
      // un-bound the loop this guard exists to bound.
      try {
        reload();
      } catch (e) {
        return;
      }
      reloadInFlight = true;
      event.preventDefault();
    }
  };
  window.addEventListener('vite:preloadError', handler);
  // Returned for tests; the app installs once for the page's lifetime.
  return () =>
    window.removeEventListener('vite:preloadError', handler);
}
